import client from './client'
import type { ApiResponse, AuthResponse, User, LoginCredentials, UserRole } from '@/types'

export interface CheckUserResult {
  exists: boolean
  has_password?: boolean
  role?: UserRole | string
  /** How many boxes the PIN input renders — answered by the server, which is
   *  the only side that knows which group the phone belongs to. An attendant
   *  signs in with the same 4 digits they use at the pump, everyone else on
   *  this surface with 6. NEVER keep a role→length map here: the API resolves
   *  it from one helper so every client agrees. Absent on a stale API. */
  pin_length?: number
  /** Ops only: the operator has no portal PIN yet (or it was cleared). They
   *  cannot set one from here — the admin portal owns that step. */
  needs_portal_migration?: boolean
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await client.post<ApiResponse<AuthResponse>>('/auth/login', credentials)
    return response.data.data!
  },

  refresh: async (refreshToken: string): Promise<{ access_token: string; refresh_token: string }> => {
    const response = await client.post<ApiResponse<{ access_token: string; refresh_token: string }>>(
      '/auth/refresh',
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    )
    return response.data.data!
  },

  getMe: async (): Promise<User> => {
    const response = await client.get<ApiResponse<User>>('/auth/me')
    return response.data.data!
  },

  updateMe: async (data: Partial<User>): Promise<User> => {
    const response = await client.put<ApiResponse<User>>('/auth/me', data)
    return response.data.data!
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await client.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
  },

  // Web PIN+OTP authentication

  /**
   * `phone` may carry the `-ops` suffix — the platform-support marker. The
   * browser is on the sacco's own domain, so the phone field is the only
   * thing that can say "this is an operator": the API strips the suffix and
   * looks the caller up in the platform band instead of this tenant.
   */
  checkUser: async (phone: string): Promise<CheckUserResult> => {
    const response = await client.post<ApiResponse<CheckUserResult>>(
      '/auth/check',
      { phone }
    )
    return response.data.data!
  },

  loginWithPin: async (phone: string, pin: string): Promise<{ otp_sent: boolean; expires_in: number; phone_masked: string }> => {
    const response = await client.post<ApiResponse<{ otp_sent: boolean; expires_in: number; phone_masked: string }>>(
      '/auth/login/pin',
      { phone, pin }
    )
    return response.data.data!
  },

  verifyLoginOtp: async (phone: string, code: string): Promise<AuthResponse> => {
    const response = await client.post<ApiResponse<AuthResponse>>(
      '/auth/login/verify-otp',
      { phone, code }
    )
    return response.data.data!
  },

  setupPin: async (phone: string, pin: string, confirmPin: string): Promise<void> => {
    await client.post('/auth/setup-pin', {
      phone,
      pin,
      confirm_pin: confirmPin,
    })
  },

  forgotPin: async (phone: string): Promise<void> => {
    await client.post('/auth/forgot-pin', { phone })
  },

  forgotPinVerify: async (phone: string, code: string): Promise<{ reset_token: string }> => {
    const response = await client.post<ApiResponse<{ reset_token: string }>>(
      '/auth/forgot-pin/verify',
      { phone, code }
    )
    return response.data.data!
  },

  forgotPinSetPin: async (phone: string, resetToken: string, pin: string, confirmPin: string): Promise<void> => {
    await client.post('/auth/forgot-pin/set-pin', {
      phone,
      reset_token: resetToken,
      pin,
      confirm_pin: confirmPin,
    })
  },
}
